import React, { useEffect, useRef, useState } from 'react';
import './chat.css';

const SIZE = 52;
const EDGE = 16;
const TOP_LIMIT = 76;      // stays under the header
const DRAG_PX = 6;         // less than this is a tap
const KEY = 'bdb_chat_fab';

const readSpot = () => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    if (s && (s.side === 'left' || s.side === 'right') && Number.isFinite(s.y)) return s;
  } catch { /* back to the default spot */ }
  return { side: 'left', y: null };
};
const clampY = (y) => Math.max(TOP_LIMIT, Math.min(y, window.innerHeight - SIZE - EDGE));
const sideX = (side) => (side === 'left' ? EDGE : window.innerWidth - SIZE - EDGE);

// the chat bubble: tap to open, drag it anywhere, it snaps to the nearest side and stays there next visit.
// Starts bottom-left: the odds and the bet slip are on the right.
export default function ChatFab({ unread, onOpen }) {
  const [spot, setSpot] = useState(readSpot);
  const [drag, setDrag] = useState(null);      // { x, y } while it follows the finger
  const [, setResized] = useState(0);
  const start = useRef(null);
  const moved = useRef(false);

  useEffect(() => {
    const onResize = () => setResized((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const y = clampY(spot.y ?? window.innerHeight);
  const x = sideX(spot.side);

  const down = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    start.current = { px: e.clientX, py: e.clientY, x, y };
    moved.current = false;
  };
  const move = (e) => {
    if (!start.current) return;
    const dx = e.clientX - start.current.px;
    const dy = e.clientY - start.current.py;
    if (!moved.current && Math.hypot(dx, dy) < DRAG_PX) return;
    moved.current = true;
    setDrag({ x: Math.max(0, Math.min(start.current.x + dx, window.innerWidth - SIZE)), y: clampY(start.current.y + dy) });
  };
  const up = (e) => {
    if (!start.current) return;
    start.current = null;
    if (!moved.current) return;
    const next = { side: e.clientX < window.innerWidth / 2 ? 'left' : 'right', y: clampY(e.clientY - SIZE / 2) };
    setDrag(null);
    setSpot(next);
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* it just won't be remembered */ }
  };
  const click = () => { if (moved.current) { moved.current = false; return; } onOpen(); };

  const at = drag || { x, y };
  return (
    <button
      type="button"
      className={`chat-fab${drag ? ' dragging' : ''}`}
      style={{ transform: `translate(${at.x}px, ${at.y}px)` }}
      aria-label="Chat"
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onClick={click}
    >
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-8 8H4l2.3-3.1A8 8 0 1 1 21 12z" /></svg>
      {unread > 0 && <span className="chat-fab-badge" key={unread}>{unread}</span>}
    </button>
  );
}
